// src/ine/components/ModalINEUpdate.jsx
import React, { useEffect, useState, useMemo } from "react";
import { Modal } from "react-bootstrap";
import Form from "react-bootstrap/Form";
import { useINE } from "../adminINECountriesContext";
import { getWithAuth, postWithAuth, putWithAuth } from "../../../api/api";
import { API_URLS } from "../../../api/apiConfig";

const ModalINEUpdate = ({ onClose, ine_id }) => {
  const INEContext = useINE();

  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMsg] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Campos del registro (añado is_country para pedir nombres)
  const [formData, setFormData] = useState({
    source: "",
    channel: "",
    ine_name: "",
    is_country: undefined, // <- necesario para pedir los nombres
  });

  // Opciones de selects
  const [channelOptions, setChannelOptions] = useState([]); // ["RoomRaccoon", "StarRez", ...]
  const [ineNameOptions, setIneNameOptions] = useState([]); // ["Reino Unido", "A Coruña", ...]

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  // Carga el registro por id
  const loadItem = async () => {
    try {
      setIsLoading(true);
      const res = await getWithAuth(API_URLS.GET_INE_COUNTRY(ine_id));
      if (res.message === "success") {
        const d = res.data;
        setFormData({
          source: d.source ?? "",
          channel: d.channel ?? "",
          ine_name: d.ine_name ?? "",
          is_country: d.is_country, // <- imprescindible
        });
      } else {
        setShowAlert(true);
        setAlertMsg(res.detail || "No se pudo cargar el registro");
      }
    } catch (err) {
      console.error("GET INE item error:", err);
      setShowAlert(true);
      setAlertMsg("Error conectando con el servidor");
    } finally {
      setIsLoading(false);
    }
  };

  // Carga opciones de channel (GET)
  const loadChannelOptions = async () => {
    try {
      const res = await getWithAuth(API_URLS.GET_INE_CHANNEL_POSSIBLE_VALUES);
      if (res.message === "success") {
        // ajusta si tu payload es distinto (ej. res.data.values)
        const arr =
          res.data?.channel_values || res.data?.values || res.data || [];
        setChannelOptions(Array.isArray(arr) ? arr : []);
      } else {
        // no bloqueo la UI si falla
        console.warn("No se pudieron cargar los channels:", res.detail);
      }
    } catch (err) {
      console.warn("Channels fetch error:", err);
    }
  };

  // Carga opciones de ine_name (POST con is_country)
  const loadIneNameOptions = async (is_country) => {
    if (typeof is_country !== "boolean") return;
    try {
      const payload = { is_country };
      const res = await postWithAuth(
        API_URLS.GET_INE_NAME_POSSIBLE_VALUES,
        payload
      );
      if (res.message === "success") {
        // ajusta si tu payload es distinto
        const arr =
          res.data?.ine_countries_provinces ||
          res.data?.values ||
          res.data ||
          [];
        setIneNameOptions(Array.isArray(arr) ? arr : []);
      } else {
        console.warn("No se pudieron cargar los nombres INE:", res.detail);
        setIneNameOptions([]);
      }
    } catch (err) {
      console.warn("INE names fetch error:", err);
      setIneNameOptions([]);
    }
  };

  // Guardar
  const saveItem = async () => {
    try {
      setSaving(true);
      const res = await putWithAuth(API_URLS.UPDATE_INE_COUNTRY(ine_id), {
        source: formData.source,
        channel: formData.channel,
        ine_name: formData.ine_name,
        // Si tu API permite editar is_country, añade aquí
        is_country: formData.is_country,
      });
      if (res.message === "success") {
        INEContext.setMessage?.("Registro actualizado correctamente");
        INEContext.setAlertType?.("success");
        INEContext.openAlertHeader?.();
        INEContext.bumpRefresh?.(); // señal global opcional
        onClose?.();
      } else {
        setShowAlert(true);
        setAlertMsg(res.detail || "No se pudo actualizar el registro");
      }
    } catch (err) {
      console.error("UPDATE INE item error:", err);
      setShowAlert(true);
      setAlertMsg("Error conectando con el servidor");
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    saveItem();
  };

  // Montaje: cargo channels y el registro
  useEffect(() => {
    loadChannelOptions();
    loadItem();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ine_id]);

  // Cuando ya tengo is_country del registro, pido los nombres
  useEffect(() => {
    loadIneNameOptions(formData.is_country);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.is_country]);

  // Si la opción actual no existe en la lista (por cambios), la añadimos temporalmente para no perderla
  const normalizedChannelOptions = useMemo(() => {
    const set = new Set(channelOptions);
    if (formData.channel && !set.has(formData.channel)) {
      return [formData.channel, ...channelOptions];
    }
    return channelOptions;
  }, [channelOptions, formData.channel]);

  const normalizedIneNameOptions = useMemo(() => {
    const set = new Set(ineNameOptions);
    if (formData.ine_name && !set.has(formData.ine_name)) {
      return [formData.ine_name, ...ineNameOptions];
    }
    return ineNameOptions;
  }, [ineNameOptions, formData.ine_name]);

  return (
    <Modal show={true} onHide={onClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Actualizar mapeo INE</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <form onSubmit={handleSubmit}>
          {isLoading ? (
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ height: "10vh" }}
            >
              <div
                className="spinner-grow text-color-amarillo-livensa"
                style={{ width: "4rem", height: "4rem" }}
                role="status"
              />
            </div>
          ) : (
            <>
              {showAlert && (
                <div className="alert alert-danger d-flex align-items-center p-4 mb-5">
                  <i className="fa-solid fa-triangle-exclamation fs-2 text-danger me-3" />
                  <div className="d-flex flex-column">
                    <span className="mb-0 text-danger">{alertMessage}</span>
                  </div>
                </div>
              )}

              {/* SOURCE */}
              <div className="mb-5">
                <label className="form-label">Fuente</label>
                <Form.Control
                  type="text"
                  placeholder="Texto tal y como llega del canal"
                  name="source"
                  value={formData.source}
                  onChange={handleInputChange}
                />
              </div>

              {/* CHANNEL (select con opciones de la API) */}
              <div className="mb-5">
                <label className="form-label">Canal</label>
                <Form.Select
                  name="channel"
                  value={formData.channel}
                  onChange={handleInputChange}
                >
                  <option value="">— Selecciona canal —</option>
                  {normalizedChannelOptions.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </Form.Select>
              </div>

              {/* INE NAME (select dependiente de is_country) */}
              <div className="mb-5">
                <div className="d-flex justify-content-between">
                  <label className="form-label">INE name</label>
                  {/* Solo lectura de is_country (muestra país/provincia) */}
                  <span className="badge bg-secondary">
                    {formData.is_country ? "País" : "Provincia / Isla"}
                  </span>
                </div>
                <Form.Select
                  name="ine_name"
                  value={formData.ine_name}
                  onChange={handleInputChange}
                  disabled={typeof formData.is_country !== "boolean"}
                >
                  <option value="">
                    {typeof formData.is_country !== "boolean"
                      ? "— Cargando tipo… —"
                      : "— Selecciona valor INE —"}
                  </option>
                  {normalizedIneNameOptions.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </Form.Select>
                {typeof formData.is_country !== "boolean" && (
                  <div className="form-text">
                    Esperando datos del registro para determinar si es país o
                    provincia/isla.
                  </div>
                )}
              </div>
            </>
          )}

          <div className="modal-footer flex-center px-0">
            <button
              type="button"
              className="btn btn-light me-3"
              onClick={onClose}
            >
              Cerrar
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={saving || isLoading}
            >
              {saving ? "Guardando…" : "Guardar cambios"}
            </button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
};

export default ModalINEUpdate;
